import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, ArrowRight, Star, Search, Truck, ShoppingCart, Car
} from 'lucide-react';
import { Shirt } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { servicesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ServicesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Read category from URL params, default to 'all'
  const categoryFromUrl = searchParams.get('category') || 'all';
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl);
  const [searchQuery, setSearchQuery] = useState('');

  // Category data with hero images and descriptions
  // NC HELP HUB - Local Rides & Everyday Services
  // PRIMARY: Rides, EVERYDAY: Courier, Errands, Laundry (NO CLEANING)
  const categories = [
    { 
      id: 'all', 
      name: 'All Services', 
      icon: Star, 
      color: '#D4AF37',
      heroImage: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1200&q=80',
      tagline: 'Local Help. One Simple Hub.',
      description: 'Rides, courier, errands, and laundry services'
    },
    { 
      id: 'rides', 
      name: 'Rides', 
      icon: Car, 
      color: '#8B5CF6',
      heroImage: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1200&q=80',
      tagline: 'Safe & Reliable Transportation',
      description: 'Book a ride with trusted drivers for appointments, errands & more',
      isPrimary: true
    },
    { 
      id: 'courier', 
      name: 'Courier', 
      icon: Truck, 
      color: '#3B82F6',
      heroImage: 'https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=1200&q=80',
      tagline: 'Fast & Reliable Delivery',
      description: 'Same-day package, document, and delivery services'
    },
    { 
      id: 'errands', 
      name: 'Errands & Tasks', 
      icon: ShoppingCart, 
      color: '#EC4899',
      heroImage: 'https://images.pexels.com/photos/6868791/pexels-photo-6868791.jpeg?auto=compress&cs=tinysrgb&w=1200',
      tagline: 'We Handle Your To-Do List',
      description: 'Grocery pickup, prescriptions, returns & more'
    },
    { 
      id: 'laundry', 
      name: 'Laundry', 
      icon: Shirt, 
      color: '#F4C2C2',
      heroImage: 'https://images.pexels.com/photos/28576623/pexels-photo-28576623.jpeg?auto=compress&cs=tinysrgb&w=1200',
      tagline: 'Fresh & Folded',
      description: 'Wash, dry, fold with pickup and delivery'
    },
  ];

  // Get current category data
  const currentCategory = categories.find(c => c.id === selectedCategory) || categories[0];

  // Update selected category when URL changes
  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat && categories.some(c => c.id === cat)) {
      setSelectedCategory(cat);
    }
  }, [searchParams]);

  // Update URL when category changes
  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    if (catId === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', catId);
    }
    setSearchParams(searchParams);
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const params = selectedCategory !== 'all' ? { service_type: selectedCategory } : {};
        const response = await servicesAPI.getAll(params);
        setServices(response.data.filter(s => s.is_active && s.package_type !== 'add_on'));
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, [selectedCategory]);

  const filteredServices = services.filter(service => 
    service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBookService = (serviceId) => {
    navigate(`/customer/book/${serviceId}`);
  };

  const getCategoryIcon = (type) => {
    const cat = categories.find(c => c.id === type);
    return cat ? cat.icon : Star;
  };

  const getCategoryColor = (type) => {
    const cat = categories.find(c => c.id === type);
    return cat ? cat.color : '#D4AF37';
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[var(--background)]/95 backdrop-blur-md border-b border-white/5">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37] flex items-center justify-center">
              <span className="text-black font-bold text-lg">H</span>
            </div>
            <span className="text-xl font-bold text-white font-['Cormorant_Garamond']">NC Help Hub</span>
          </Link>
          
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link to="/customer/dashboard">
                <Button className="btn-primary">My Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-white/70 hover:text-white">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary">Create Account</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {/* Hero Banner for Selected Category */}
        <motion.div
          key={selectedCategory}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative rounded-3xl overflow-hidden mb-10"
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src={currentCategory.heroImage}
              alt={currentCategory.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
          </div>
          
          {/* Content */}
          <div className="relative z-10 px-8 py-12 md:px-12 md:py-16">
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${currentCategory.color}25` }}
              >
                <currentCategory.icon 
                  className="w-7 h-7" 
                  style={{ color: currentCategory.color }} 
                />
              </div>
              <span 
                className="text-sm font-semibold px-4 py-1.5 rounded-full"
                style={{ backgroundColor: `${currentCategory.color}25`, color: currentCategory.color }}
              >
                {currentCategory.name}
              </span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 font-['Cormorant_Garamond']">
              {currentCategory.tagline}
            </h1>
            <p className="text-white/70 text-lg md:text-xl max-w-xl">
              {currentCategory.description}
            </p>
            
            {/* Quick stat */}
            <div className="mt-6 flex items-center gap-6">
              <div className="flex items-center gap-2 text-white/60">
                <Star className="w-5 h-5 text-[#D4AF37]" fill="#D4AF37" />
                <span className="font-medium text-white">{filteredServices.length}</span>
                <span>services available</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <Input
              type="text"
              placeholder="Search services..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-xl bg-[#111111] border-white/10 text-white"
            />
          </div>
        </div>

        {/* Category Tabs with Preview Images */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isSelected = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                data-testid={`category-${cat.id}`}
                className={`group relative flex flex-col items-center gap-2 p-3 rounded-2xl transition-all ${
                  isSelected
                    ? 'bg-[#D4AF37]/20 ring-2 ring-[#D4AF37]'
                    : 'bg-white/5 hover:bg-white/10'
                }`}
                style={{ minWidth: '100px' }}
              >
                {/* Category Image Thumbnail */}
                <div className="relative w-16 h-16 rounded-xl overflow-hidden">
                  <img 
                    src={cat.heroImage}
                    alt={cat.name}
                    className={`w-full h-full object-cover transition-all ${
                      isSelected ? 'opacity-100' : 'opacity-60 group-hover:opacity-80'
                    }`}
                  />
                  <div className={`absolute inset-0 flex items-center justify-center ${
                    isSelected ? 'bg-black/30' : 'bg-black/50 group-hover:bg-black/40'
                  }`}>
                    <Icon 
                      className="w-6 h-6 transition-transform group-hover:scale-110" 
                      style={{ color: isSelected ? '#D4AF37' : 'white' }}
                    />
                  </div>
                </div>
                
                {/* Category Name */}
                <span className={`text-xs font-medium transition-colors ${
                  isSelected ? 'text-[#D4AF37]' : 'text-white/70 group-hover:text-white'
                }`}>
                  {cat.name}
                </span>
              </button>
            );
          })}
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/50">Loading services...</p>
          </div>
        ) : selectedCategory === 'rides' ? (
          /* Rides Category - Show ride booking card */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card p-6 hover:ring-2 hover:ring-purple-500/50 transition-all cursor-pointer group"
              onClick={() => navigate('/book-ride')}
              data-testid="ride-service-card"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-500/15">
                  <Car className="w-6 h-6 text-purple-500" />
                </div>
                <span className="text-xs px-3 py-1 rounded-full font-medium bg-purple-500/15 text-purple-400">
                  Transportation
                </span>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                Book a Ride
              </h3>
              <p className="text-white/60 text-sm mb-4">
                Safe, reliable transportation with trusted drivers. Perfect for appointments, errands, airport trips & more.
              </p>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                  One-way or round trip
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                  Sedan, SUV, or Van
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                  Card or Cash payment
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <span className="text-lg font-bold text-purple-400">
                  From $10
                </span>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="book-ride-btn"
                >
                  Book Ride <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/50 text-lg">No services found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service, idx) => {
              const CategoryIcon = getCategoryIcon(service.service_type);
              const categoryColor = getCategoryColor(service.service_type);
              
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="card p-6 hover:ring-2 hover:ring-[#D4AF37]/50 transition-all cursor-pointer group"
                  onClick={() => handleBookService(service.id)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${categoryColor}15` }}
                    >
                      <CategoryIcon className="w-6 h-6" style={{ color: categoryColor }} />
                    </div>
                    <span 
                      className="text-xs px-3 py-1 rounded-full font-medium"
                      style={{ backgroundColor: `${categoryColor}15`, color: categoryColor }}
                    >
                      {service.service_type === 'errands' ? 'Errands & Tasks' : 
                       service.service_type?.charAt(0).toUpperCase() + service.service_type?.slice(1)}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                    {service.name}
                  </h3>
                  <p className="text-white/60 text-sm mb-4 line-clamp-2">
                    {service.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-white/50 text-sm">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {service.duration_hours}h
                      </span>
                    </div>
                    <span className="text-2xl font-bold" style={{ color: categoryColor }}>
                      ${service.hourly_rate}
                      <span className="text-sm font-normal text-white/50">/hr</span>
                    </span>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <Button 
                      className="w-full rounded-full gap-2"
                      style={{ backgroundColor: categoryColor, color: '#000' }}
                    >
                      Book Now
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              );
            })}
            
            {/* Always show Rides card in All Services view */}
            {selectedCategory === 'all' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: filteredServices.length * 0.05 }}
                className="card p-6 hover:ring-2 hover:ring-purple-500/50 transition-all cursor-pointer group"
                onClick={() => navigate('/book-ride')}
                data-testid="ride-service-card-all"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-500/15">
                    <Car className="w-6 h-6 text-purple-500" />
                  </div>
                  <span className="text-xs px-3 py-1 rounded-full font-medium bg-purple-500/15 text-purple-400">
                    Transportation
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                  Book a Ride
                </h3>
                <p className="text-white/60 text-sm mb-4 line-clamp-2">
                  Safe, reliable transportation with trusted drivers for appointments, errands & more.
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-white/50 text-sm">
                    <span className="flex items-center gap-1">
                      <Car className="w-4 h-4" />
                      On-demand
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-purple-400">
                    $10+
                  </span>
                </div>
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <Button 
                    className="w-full rounded-full gap-2 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    Book Ride
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* Guest Checkout Info */}
        <div className="mt-16 text-center">
          <div className="card p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-white mb-2">No Account Needed</h3>
            <p className="text-white/60 mb-4">
              You can book services as a guest. Simply select a service and fill in your contact details.
            </p>
            <p className="text-sm text-white/40">
              Want to track bookings and earn rewards?{' '}
              <Link to="/register" className="text-[#D4AF37] hover:underline">Create a free account</Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServicesPage;
